export type AudienceId = "kids" | "family" | "party" | "hobby" | "hardcore" | "collectors" | "roleplayers";
export const AUDIENCES: AudienceId[] = ["kids", "family", "party", "hobby", "hardcore", "collectors", "roleplayers"];

export type SliderId = "depth" | "access" | "immersion" | "interaction" | "replay" | "art" | "components" | "speed";
export const SLIDERS: SliderId[] = ["depth", "access", "immersion", "interaction", "replay", "art", "components", "speed"];
export type Focus = Record<SliderId, number>;

export type PartId = "cards" | "board" | "dice" | "tokens" | "minis" | "box" | "rulebook" | "insert" | "extras";
export const PARTS: PartId[] = ["cards", "board", "dice", "tokens", "minis", "box", "rulebook", "insert", "extras"];

export type StatId = "creativity" | "systems" | "art" | "writing" | "craft" | "charisma";
export const STATS: StatId[] = ["creativity", "systems", "art", "writing", "craft", "charisma"];

export type FitMap = Record<AudienceId, number>;

/** Simulation profile of a design: what the playtest table and reviewers react to. */
export interface Profile {
  luck: number;
  decisions: number;
  interaction: number;
  turnMin: number;
  catchUp: number;
  complexity: number;
  asymmetry: number;
  powerSpread: number;
  setup: number;
  lengthMin: number;
}

export interface Unlockable {
  id: string;
  name: string;
  year: number;
  start?: boolean;
  research?: number;
  hidden?: boolean;
  unlockHint?: string;
}

export interface Mechanic extends Unlockable {
  formats: string[];
  tags: string[];
  audience: FitMap;
  profile: Profile;
  ideal: Focus;
  requires?: string[];
  blurb: string;
}

export interface Theme extends Unlockable {
  tags: string[];
  audience: FitMap;
  palette: string[];
  motif: string;
}

export interface Audience extends Unlockable {
  priceCeiling: number;
  playTimeLimit: number;
  players: [number, number];
  decayWeeks: number;
  luckTolerance: number;
  ideal: Focus;
  size: [number, number][];
}

export interface Format extends Unlockable {
  baseCost: number;
  baseValue: number;
  parts: PartId[];
  work: number;
  writingHeavy?: boolean;
  live?: string;
}

export interface ComponentOption {
  id: string;
  part: PartId;
  name: string;
  year: number;
  research?: number;
  cost: number;
  value: number;
  setup: number;
  tier: number;
  perPallet?: number;
  clarity?: number;
  durability?: number;
  replay?: number;
  shelf?: number;
  hobbyBoost?: number;
  needsRoom?: string;
  unlocksMechanics?: string[];
}

export interface Era {
  id: string;
  name: string;
  from: number;
  to: number;
  buyers: string;
  music: string[];
  blurb: string;
}

export type ReviewInput = "fit" | "focus" | "polish" | "value" | "trend";
export interface Reviewer {
  id: string;
  name: string;
  year: number;
  loves: string;
  hates: string;
  weights: Record<ReviewInput, number>;
  taste: Record<string, number>;
  voice: string[];
  /** a deeper bank: by score band, for follow-ups, and about the review's weakest part */
  quotes?: { great: string[]; good: string[]; mixed: string[]; bad: string[]; sequelGood: string[]; sequelBad: string[]; weak: Record<string, string> };
}

export interface Channel extends Unlockable {
  margin: number;
  returns: number;
  best: AudienceId[];
  reach: FitMap;
  catch: string;
  seasonal?: boolean;
  fanScaled?: boolean;
  preorder?: boolean;
  buzz?: number;
}

export interface Factory extends Unlockable {
  costMult: number;
  setup: number;
  minOrder: number;
  leadWeeks: number;
  quality: number;
  defect: number;
  qualitySpread: number;
  shipping?: boolean;
  needsRoom?: string;
  upgradeQuality?: number;
}

export interface Slider {
  id: SliderId;
  name: string;
  pushes: string;
  costs: string;
}

export type ProblemId =
  | "runaway" | "kingmaker" | "ap" | "downtime" | "too_random" | "solved" | "broken_card" | "rules_confusion" | "drags" | "fiddly";
export interface Problem {
  id: ProblemId;
  name: string;
  see: string;
  fix: string;
  fixWeeks: number;
  fixEffect: Record<string, number>;
  fixCost: string;
  sees: string[];
}

export type PersonalityId = "analyst" | "social" | "kid" | "shark" | "casual" | "rules_lawyer";
export interface Personality {
  id: PersonalityId;
  name: string;
  likes: Record<string, number>;
  lines: Record<string, string[]>;
}

export interface TesterPool extends Unlockable {
  cost: number;
  accuracy: number;
  kindness: number;
  testers: [number, number];
  note: string;
  needsRoom?: string;
}

export interface Role {
  id: string;
  name: string;
  main: StatId;
  does: string;
  year: number;
  salary: number;
}

export interface Trait {
  id: string;
  name: string;
  kind: "work" | "theme" | "social" | "quirk";
  desc: string;
  hidden?: boolean;
  year?: number;
  fx: Record<string, any>;
  clash?: string[];
  click?: string[];
}

/** An extra building you buy on your lot once you're big enough. */
export interface Annex {
  id: string;
  name: string;
  level: StudioLevel["id"];
  tilesW: number;
  tilesH: number;
  staffCap: number;
  rent: number;
  cost: number;
  blurb: string;
  rooms: string[];
  /** not for sale before this year */
  year?: number;
  /** what owning it does: fans (+share per sale) and reputation (+points each January) */
  fx?: { fans?: number; reputation?: number };
}

export interface StudioLevel {
  id: "garage" | "office" | "studio" | "hq";
  name: string;
  tilesW: number;
  tilesH: number;
  staffCap: number;
  rent: number;
  moveCost: number;
  pallets: number;
  floors?: number;
  rooms: string[];
}

export interface RoomType {
  id: string;
  name: string;
  level: StudioLevel["id"];
  cost: number;
  minW: number;
  minH: number;
  effect: string;
  fx: Record<string, any>;
  palette: { wall: string; floor: string };
  furniture: string[];
  /** what a new room comes furnished with: workstations in rows (one person each), a centrepiece, then decor */
  /** feature: centrepieces placed first; signature: the room's tell-tale piece, placed after the desks */
  kit: { desks: string[]; feature: string[]; decor: string[]; signature?: string[] };
}

export interface Rival {
  id: string;
  name: string;
  personality: string;
  will: string;
  audiences: AudienceId[];
  themes: string[];
  mechanics: string[];
  quality: number;
  spread: number;
  cadence: number;
  copycat: number;
  priceWar: number;
  cash: number;
  from?: number;
  acquirer?: boolean;
  parody?: boolean;
  /** a newcomer that founds itself later, when the market has room (see market.ts) */
  upstart?: boolean;
  litigious?: boolean;
  color: string;
}

export interface Combo {
  mechanic: string;
  theme: string;
  audience: AudienceId;
  name: string;
}

export interface FurnitureKind {
  id: string;
  name: string;
  cost: number;
  w: number;
  h: number;
  seat?: boolean;
  wall?: boolean;
  floor?: boolean;
  plant?: boolean;
  light?: number;
  storage?: number;
  year?: number;
  /** a reward: unlocked by this many clean sweeps (all three yearly goals met), never bought */
  sweeps?: number;
  fx: { morale?: number; speed?: number; quality?: number; radius?: number; [k: string]: number | undefined };
}

export interface PetBreed {
  id: string;
  name: string;
  kind: "cat" | "dog";
  colors: string[];
}

export interface MarketingKind {
  id: string;
  name: string;
  year: number;
  cost: number;
  hype: number;
  best: AudienceId[];
}

export interface Effect {
  type: string;
  [k: string]: any;
}

export interface EventOption {
  id: string;
  label: string;
  detail?: string;
  effects: Effect[];
}

export interface EventDef {
  id: string;
  kind: "industry" | "culture" | "people" | "weird" | "opportunity" | "crisis";
  title: string;
  text: string;
  when: { minYear?: number; maxYear?: number; needs?: string[]; tension?: "bored" | "struggling" | "winning"; maxPets?: number; season?: boolean };
  weight: number;
  cooldownYears?: number;
  once?: boolean;
  options: EventOption[];
}

export interface ArcOption extends Omit<EventOption, "effects"> {
  effects?: Effect[];
  next?: string;
  delayWeeks?: number;
  ending?: string;
  requires?: "check";
}

export interface ArcStep {
  id: string;
  title: string;
  text: string;
  check?: { releasedTheme?: string; withinWeeks?: number };
  options: ArcOption[];
}

export interface Arc {
  id: string;
  name: string;
  when: { minYear?: number; maxYear?: number; needs?: string[] };
  steps: ArcStep[];
}

export interface Achievement {
  id: string;
  name: string;
  desc: string;
  hidden: boolean;
  cond: Record<string, any>;
}

export interface Legend {
  id: string;
  name: string;
  role: string;
  signature: string;
  year: number;
  stats: Partial<Record<StatId, number>>;
  salary: number;
  demand: string;
  quirk: string;
}

export interface Convention {
  id: string;
  name: string;
  year: number;
  week: number;
  size: number;
  /** what a booth costs (1965 dollars) */
  booth?: number;
  awards: string[];
}

export interface AwardDef {
  id: string;
  name: string;
  rule: "best" | "newcomer" | "art" | "crowd" | "value" | "novel" | "replay";
  audience?: AudienceId[];
  format?: string[];
}

/** A one-time card that introduces a system the first time it matters. */
export interface TipDef {
  id: string;
  text: string;
  action: string;
}

/** A yearly goal template: {n} is filled in for your company's size. */
export interface GoalDef {
  id: string;
  text: string;
  metric: string;
  from: number;
  reward: { research?: number; cashShare?: number; rep?: number; fans?: number };
  /** a goal only for companies that chose this direction (added each year as a fourth goal) */
  direction?: string;
}

/** The direction you choose for your company in year three. */
export interface DirectionDef {
  id: string;
  name: string;
  blurb: string;
  fx: Record<string, number>;
}

/** A finale title, given to whoever tops its stat. */
export interface FinaleTitle {
  id: string;
  title: string;
  why: string;
  stat: string;
}

/** A shop of your own in another town: costs a lot up front, earns every week, grows your fans and reach. */
export interface StoreDef {
  id: string;
  name: string;
  blurb: string;
  cost: number;
  income: number;
  fans: number;
  reach: number;
  year: number;
}

/** How big a game is: more work, more sales, a dearer box. */
export interface GameSize {
  id: "filler" | "standard" | "big" | "epic";
  name: string;
  blurb: string;
  work: number;
  sales: number;
  value: number;
  ceiling: number;
  level: StudioLevel["id"];
  year: number;
}

export interface MusicTrack {
  id: string;
  era: string;
  name: string;
  bpm: number;
  key: string;
  mood: string;
  lead: string;
  progression: number[];
}

export interface GardenPlant {
  id: string;
  name: string;
  leaf: [number, number, number];
  flower?: [number, number, number];
  weeks: number;
  morale: number;
}

export interface Perk {
  id: string;
  branch: "mechanics" | "components" | "business" | "studio";
  name: string;
  era: string;
  cost: number;
  fx: Record<string, number>;
  desc: string;
  /** perks you need first */
  requires?: string[];
  /** either-or: taking this rules these out */
  excludes?: string[];
}

export interface LicenceDef {
  id: string;
  name: string;
  kind: "film" | "tv" | "books" | "cartoon";
  theme: string;
  audience: AudienceId;
  year: number;
  fee: number; // 1965 dollars
  deadlineWeeks: number;
  flop: number; // chance the film or show flops
  boost: number; // demand multiplier while it's popular
  text: string;
}

export interface CardRules {
  rarities: { id: string; name: string; share: number; power: [number, number] }[];
  archetypes: { id: string; name: string; desc: string }[];
  keywords: { id: string; name: string; archetype: string }[];
  setSize: number;
  powerBudget: number;
  cardWords: { first: string[]; second: string[] };
}

export interface NgPlus {
  title: string;
  intro: string;
  era: Era;
  yearShift: Record<string, number>; // unlock id -> years earlier (negative) or later
  audienceMult: Partial<Record<AudienceId, number>>;
  rivalCadence: number;
  startResearch: number;
  rules: string[];
}

export interface NameLists {
  adjectives: string[];
  nouns: string[];
  places: string[];
  /** patterns using {adj}, {noun}, {theme}, {theme2}, {place} */
  forms: string[];
  /** words for each theme id */
  themes: Record<string, string[]>;
  expansions: string[];
  spinoffs: string[];
  supplements: string[];
}

/** A research project you can run again and again: it never runs out. */
export interface StudyDef {
  id: string;
  name: string;
  /** {n} = how many it reveals, {mechanic} = the mechanic it improves */
  desc: string;
  branch: "mechanics" | "components" | "business" | "studio";
  cost: number;
  reveals: number;
  effect: "fits" | "audiences" | "mastery";
}

/** A strength a person picks when they reach a level: one of two, for good. */
export interface StaffPerkTier {
  level: number;
  options: { id: string; name: string; desc: string; fx: Record<string, number> }[];
}

/** A check-in halfway through a phase: two directions for the game, each with a small effect. */
export interface CheckinDef {
  id: string;
  phase: "prototype" | "art";
  title: string;
  text: string;
  /** fx: quality (prototype work quality), artQuality, replay (designed replay), hype, work (prototype work need), artWork (art work need) */
  options: { id: string; label: string; detail: string; fx: Record<string, number> }[];
  /** only for games that match: formats, audiences, box sizes, and years */
  when?: { formats?: string[]; audiences?: string[]; scopes?: string[]; from?: number; to?: number };
}

/** A bigger strategic choice at the start of each decade (decades.json). */
export interface DecadeOption {
  id: string;
  label: string;
  /** the line under the choice; {cash}, {fans}, {research} and {royalty} are filled with the scaled amounts */
  detail: string;
  /** what the log says after the company name ("backs the toy chains for the seventies.") */
  log: string;
  effects: Effect[];
}

export interface DecadeDef {
  id: string;
  year: number;
  title: string;
  text: string;
  default: string;
  options: DecadeOption[];
}

/** A fair where you can book a booth, but which gives no awards (conventions.json "shows"). */
export interface ShowDef {
  id: string;
  name: string;
  year: number;
  week: number;
  size: number;
  booth: number;
}

export interface FanLetter {
  id: string;
  band: "high" | "mid" | "low";
  audience?: AudienceId[];
  text: string;
}

export interface FanAskDef {
  title: string;
  text: string;
  promise: string;
  promiseDetail: string;
}

export interface FanMailContent {
  letters: FanLetter[];
  asks: Record<"sequel" | "expansion" | "reprint", FanAskDef>;
  thanks: string;
  thanksDetail: string;
  lines: Record<"letter" | "promised" | "thanked" | "kept" | "broken", string>;
}
